/* eslint-disable camelcase */
import { Accordion, Badge, Stack, Text } from '@mantine/core';
import type { TSearchResult } from '@newo/api/gateway';
import { Link } from 'react-router-dom';
import { MatchesList } from './MatchesList';
import { PATHS } from '@/const';
import { getTypeBadgeColor } from '@/components/SearchResultsContent/SearchResultsContent.utils';

interface SearchResultItemProps {
    result: TSearchResult;
    index: number;
    searchParams: URLSearchParams;
}

export const SearchResultItem = ({
    result,
    index,
    searchParams
}: SearchResultItemProps) => {
    const { project_idn, type, data, result_matches } = result;
    const uniqueKey = `${data.project_id}-${data.id}-${index}`;

    const basePath = data.flow_id
        ? PATHS.agent.builder.replace(':flowId', data.flow_id)
        : `${PATHS.projects}/${data.project_id}`;

    const linkPath = searchParams.toString()
        ? `${basePath}?${searchParams.toString()}`
        : basePath;

    const linkText = data.flow_id ? 'Open in Builder' : 'View Project';
    const linkLabel = data.flow_id
        ? `Open flow ${data.flow_idn} in builder`
        : `View project ${project_idn}`;

    return (
        <Accordion.Item key={uniqueKey} value={uniqueKey}>
            <Accordion.Control>
                <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-2">
                        <Badge
                            color={getTypeBadgeColor(type)}
                            size="sm"
                            variant="filled"
                        >
                            {type}
                        </Badge>
                        <Text fw={500} size="sm">
                            {data.idn}
                        </Text>
                    </div>
                    <Text c="dimmed" size="xs">
                        {project_idn} / {data.agent_idn ?? 'N/A'} /{' '}
                        {data.flow_idn ?? 'N/A'}
                    </Text>
                </div>
            </Accordion.Control>
            <Accordion.Panel>
                <Stack gap="sm">
                    <div className="flex gap-2 flex-wrap">
                        <Badge size="sm" variant="light">
                            Project: {project_idn}
                        </Badge>
                        {Boolean(data.agent_idn) && (
                            <Badge color="blue" size="sm" variant="light">
                                Agent: {data.agent_idn}
                            </Badge>
                        )}
                        {Boolean(data.flow_idn) && (
                            <Badge color="green" size="sm" variant="light">
                                Flow: {data.flow_idn}
                            </Badge>
                        )}
                    </div>

                    <MatchesList matches={result_matches} />

                    <Link
                        aria-label={linkLabel}
                        className="text-blue-6 hover:underline text-sm"
                        to={linkPath}
                    >
                        {linkText} →
                    </Link>
                </Stack>
            </Accordion.Panel>
        </Accordion.Item>
    );
};
